import { Box, TextField, Button, IconButton } from '@mui/material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { IAuthResponse } from '../../interfaces/user';
import IResponse from '../../interfaces/response';
import axios from 'axios';
import { IRAN_MOBILE_REGEX } from '../../constants/regex';
import useToken from '../../hooks/useToken';

const loginSchema = z.object({
	emailOrPhone: z.string().refine(value => z.string().email().safeParse(value).success || IRAN_MOBILE_REGEX.test(value), { message: 'invalid_email_or_phone_message' }),
	password: z.string().min(6, 'invalid_password_message'),
});

const Login = () => {
	const [showPassword, setShowPassword] = useState(false);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { setToken } = useToken();
	const [searchParams] = useSearchParams();
	const route = searchParams.get('route');

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<z.infer<typeof loginSchema>>({
		resolver: zodResolver(loginSchema),
	});

	const toggleShowPassword = () => {
		setShowPassword(old => !old);
	};

	const handleLogin = (data: z.infer<typeof loginSchema>) => {
		axios<IResponse<IAuthResponse>>({
			url: '/users/login',
			method: 'POST',
			data: {
				login: data?.emailOrPhone,
				password: data?.password,
			},
		}).then(res => {
			if (res.data.success) {
				setToken(res.data.data.token);
				if (route === 'campaigns/create') {
					navigate(`/${route}`);
				} else {
					navigate('/');
				}
			}
		});
	};

	return (
		<>
			<form className="flex w-full flex-col gap-y-6" onSubmit={handleSubmit(handleLogin)}>
				<TextField className="w-full" id="outlined-required" label={t('email-input-placeholder')} {...register('emailOrPhone')} error={!!errors.emailOrPhone} helperText={t(errors.emailOrPhone?.message!)} />
				<TextField
					InputProps={{
						endAdornment: (
							<IconButton onClick={toggleShowPassword} className="text-text-secondary">
								{showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
							</IconButton>
						),
					}}
					className="w-full"
					id="outlined-password-input"
					label={t('password')}
					type={showPassword ? 'text' : 'password'}
					autoComplete="current-password"
					{...register('password')}
					error={!!errors.password}
					helperText={t(errors.password?.message!)}
				/>
				<Button type="submit" size="large" variant="contained" className="text-base font-normal">
					{t('login')}
				</Button>
			</form>
			<Box className="mb-7 mt-6 flex justify-between text-primary-main">
				<Link to={'/auth/recover-password'}>{t('recover-password')}</Link>
				<Link to={'/auth/register'}>{t('sign_up')}</Link>
			</Box>
		</>
	);
};

export default Login;
